import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { checkDailyrentPrice } from 'shared/actions/checkDailyrentPrice/actions';
import {
  clearBookingDates,
  resetBookingDatesFromCalendar,
  setDates,
  setError,
} from 'shared/actions/dailyrentBookingCalendar';
import {
  CHECKOUT_ONLY_TOOLTIP_MESSAGE,
  DEFAULT_INPUT_TEXT,
  updateDailyrentUrlParameters,
} from 'shared/containers/BookingCalendarInputContainer/helpers';
import { useCalendarLifecycle } from 'shared/hooks/useCalendarLifecycle';
import { selectOfferId } from 'shared/selectors';
import {
  selectDailyrentBookingCalendar,
  selectDailyrentBookingGeneral,
  selectDailyrentBookingGuests,
} from 'shared/selectors/dailyrentBooking';
import {
  trackCalendarDatesReset,
  trackCalendarDatesSubmit,
  trackCalendarInputClick,
  trackCalendarInputShow,
  trackCalendarOpen,
  trackMinimumDurationHint,
} from 'shared/tracking/dailyrentBookingCalendar';

import type { IDateRange, ISelectionRange, IValidationError } from '@cian/frontend-dailyrent-calendar';

export function useBookingCalendarContainer() {
  const dispatch = useDispatch();
  const offerId = useSelector(selectOfferId);
  const { error, isOnlineBooking, isLoading } = useSelector(selectDailyrentBookingGeneral);
  const { dates, daysAvailability, dateInputText } = useSelector(selectDailyrentBookingCalendar);
  const { guestsCount } = useSelector(selectDailyrentBookingGuests);

  const { openCalendar, closeCalendar, isBusy, isOpen } = useCalendarLifecycle();

  const [draftValue, setDraftValue] = React.useState<IDateRange | null>(null);
  const committedValue = React.useMemo(
    () => (dates.from && dates.to ? { start: dates.from, end: dates.to } : null),
    [dates.from, dates.to],
  );

  React.useEffect(() => {
    if (!isOpen) {
      setDraftValue(null);
    }
  }, [isOpen]);

  const value = isOpen ? (draftValue ?? committedValue) : null;

  const showOverlay = isBusy;
  const tooltipMessage = error || null;

  const inputText = dateInputText || DEFAULT_INPUT_TEXT;
  const isDefaultInputText = inputText === DEFAULT_INPUT_TEXT;

  const submitDateRange = React.useCallback(
    async (start: string, end: string) => {
      const errorMessage = await dispatch(checkDailyrentPrice({ dates: { start, end }, guestsCount }));

      if (errorMessage) {
        dispatch(setError(errorMessage));

        return;
      }

      dispatch(setDates({ from: start, to: end }));

      const newUrl = updateDailyrentUrlParameters({
        currentUrl: window.location.href,
        dates: { checkin: start, checkout: end },
        guestsCount,
      });

      history.replaceState(undefined, '', newUrl);
      trackCalendarDatesSubmit(offerId);
      closeCalendar();
    },
    [dispatch, offerId, guestsCount, closeCalendar],
  );

  const onSelectionChange = React.useCallback(
    (range: ISelectionRange) => {
      if (!range.end) {
        setDraftValue({ start: range.start.date });
        dispatch(setError(range.start.status === 'checkoutOnly' ? CHECKOUT_ONLY_TOOLTIP_MESSAGE : ''));

        return;
      }

      const start = range.start.date;
      const end = range.end.date;

      setDraftValue({ start, end });
      dispatch(setError(''));
      submitDateRange(start, end);
    },
    [dispatch, submitDateRange],
  );

  const onValidationError = React.useCallback(
    (validationError: IValidationError) => {
      if (validationError.type === 'minNights') {
        trackMinimumDurationHint(offerId);
      }
    },
    [offerId],
  );

  const onInputClick = React.useCallback(() => {
    if (isBusy) {
      return;
    }

    if (!isOpen) {
      openCalendar();
      trackCalendarInputClick(offerId);
      trackCalendarOpen(offerId);

      return;
    }

    closeCalendar();
  }, [isOpen, isBusy, offerId, openCalendar, closeCalendar]);

  const onOutsideClick = React.useCallback(() => {
    if (isBusy) {
      return;
    }

    closeCalendar();
  }, [isBusy, closeCalendar]);

  const onResetClick = React.useCallback(() => {
    setDraftValue(null);
    dispatch(resetBookingDatesFromCalendar());
    trackCalendarDatesReset(offerId);
  }, [dispatch, offerId]);

  const isResetDisabled = isBusy || value === null;

  // сбрасываем даты, если пользователь зашел с датами на объявку, для которой
  // недоступно онлайн-бронирование
  // TODO: перенести в более подходящее место
  // mount-only effect: чистим URL и шлем show event один раз на показе карточки
  /* eslint-disable react-hooks/exhaustive-deps */
  React.useEffect(() => {
    if (!isOnlineBooking) {
      dispatch(clearBookingDates());
    } else if (!dates.from || !dates.to) {
      const url = new URL(window.location.href);
      if (url.searchParams.has('checkin') || url.searchParams.has('checkout')) {
        dispatch(clearBookingDates());
      }
    }

    trackCalendarInputShow(offerId);
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  return React.useMemo(
    () => ({
      isOpen,
      showOverlay,
      isLoading,
      isResetDisabled,
      daysAvailability,
      value,
      tooltipMessage,
      inputText,
      isDefaultInputText,
      onSelectionChange,
      onValidationError,
      onInputClick,
      onOutsideClick,
      onResetClick,
    }),
    [
      isOpen,
      showOverlay,
      isLoading,
      isResetDisabled,
      daysAvailability,
      value,
      tooltipMessage,
      inputText,
      isDefaultInputText,
      onSelectionChange,
      onValidationError,
      onInputClick,
      onOutsideClick,
      onResetClick,
    ],
  );
}
